import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const STATUS_LABELS = {
  pending_review: 'Pending Review',
  acknowledged_by_teacher: 'Acknowledged by Teacher',
  broadcasted_to_staff: 'Broadcasted to Staff',
  resolved: 'Resolved',
  dismissed: 'Dismissed'
};
const fmt = (d) => { try { return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return ''; } };
const EMPTY = { active: [], resolved: [], dismissed: [], counts: { active: 0, pending: 0, total: 0 } };

// Staff Conduct & Compliance Command Panel: live banner + review console with per-case controls.
export default function PrincipalComplaintsPanel({ user }) {
  const headers = { 'x-user-id': String(user?._id || user?.id || '') };
  const [data, setData] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [tabKey, setTabKey] = useState('active');
  const [notes, setNotes] = useState({});
  const [official, setOfficial] = useState({});
  const [banner, setBanner] = useState(null);
  const [err, setErr] = useState('');
  const seen = useRef(null); // ids already known; first load seeds it so old cases don't banner

  const load = useCallback(() => {
    axios.get(`${API_BASE_URL}/api/staff-feedback/principal/overview`, { headers: { 'x-user-id': String(user?._id || user?.id || '') } })
      .then((r) => {
        const d = { ...EMPTY, ...r.data };
        setData(d);
        setErr('');
        const all = [...d.active, ...d.resolved, ...d.dismissed];
        if (seen.current === null) seen.current = new Set(all.map((c) => c._id));
        else {
          const fresh = all.filter((c) => !seen.current.has(c._id));
          fresh.forEach((c) => seen.current.add(c._id));
          const latest = fresh[0];
          if (latest) setBanner(`⚠️ New Staff Complaint Logged: ${latest.reporter?.name || 'A teacher'} reported ${latest.target?.name || 'a teacher'} for ${(latest.categories || []).join(', ')}.`);
        }
      })
      .catch((e) => setErr(e.response?.data?.error || 'Could not load complaints'));
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const patch = async (id, body) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/staff-feedback/admin/${id}/update-status`, body, { headers });
      load();
    } catch (e) { setErr(e.response?.data?.error || 'Update failed'); }
  };

  const addNote = async (id, type) => {
    const text = (official[id] || '').trim();
    if (!text) { setErr('Enter a note first'); return; }
    await patch(id, { officialNote: text, noteType: type });
    setOfficial((o) => ({ ...o, [id]: '' }));
  };

  const pending = data.counts?.pending || 0;
  const list = data[tabKey] || [];
  const toggle = (label, checked, onChange) => (
    <label style={{ display: 'flex', gap: 6, fontSize: 13, alignItems: 'center' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );

  return (
    <>
      {banner && (
        <div role="alert" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', background: '#fef3c7', border: '2px solid #f59e0b', color: '#78350f', borderRadius: 10, padding: '10px 14px', marginBottom: 8, fontWeight: 700 }}>
          <span>{banner}</span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => { setOpen(true); setBanner(null); }}>Review</button>
            <button onClick={() => setBanner(null)} aria-label="Dismiss banner">×</button>
          </span>
        </div>
      )}
      <button
        onClick={() => setOpen(true)}
        style={{
          width: '100%', padding: '14px', border: 'none', borderRadius: '10px', cursor: 'pointer',
          fontSize: '16px', fontWeight: 'bold', color: 'white',
          background: pending > 0 ? '#dc2626' : '#475569'
        }}
      >
        {pending > 0 ? `🚨 ${pending} staff complaint(s) awaiting your review` : '🛡️ Staff Conduct & Compliance Command Panel'} ({data.counts?.total || 0} total)
      </button>
      {err && !open && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{err}</div>}

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 800, maxHeight: '92dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flex: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid #e2e8f0' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Staff Conduct &amp; Compliance Command Panel</h2>
              <button onClick={() => setOpen(false)} aria-label="Close" style={{ fontSize: 24, background: 'none', border: 'none', cursor: 'pointer', touchAction: 'manipulation' }}>×</button>
            </div>
            <div style={{ flex: 'none', display: 'flex', gap: 6, padding: '8px 16px', overflowX: 'auto', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
              {['active', 'resolved', 'dismissed'].map((k) => (
                <button key={k} onClick={() => setTabKey(k)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontWeight: tabKey === k ? 700 : 400, background: tabKey === k ? '#e2e8f0' : 'white', textTransform: 'capitalize' }}>
                  {k} ({data[k].length})
                </button>
              ))}
            </div>
            <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', padding: '4px 16px 24px' }}>
            {err && <div style={{ color: '#dc2626', marginTop: 8 }}>{err}</div>}
            {!list.length && <p style={{ color: '#64748b' }}>No {tabKey} complaints.</p>}
            {list.map((r) => (
              <div key={r._id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                  <b>Against: {r.target?.name || 'Unknown'}</b>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{STATUS_LABELS[r.status] || r.status}</span>
                </div>
                <div style={{ fontSize: 13, color: '#334155' }}>Reported by: <b>{r.reporter?.name || 'Unknown'}</b> · Incident: {fmt(r.incidentDate)}</div>
                <div style={{ margin: '6px 0' }}>
                  {(r.categories || []).map((c) => (
                    <span key={c} style={{ background: '#dc2626', color: 'white', borderRadius: 999, padding: '2px 8px', fontSize: 11, marginRight: 4 }}>{c}</span>
                  ))}
                </div>
                <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0' }}>{r.remarks}</p>

                {toggle(r.forcePopupForTarget ? '🔔 Target teacher mandatory pop-up: Active / Forced On' : '🔕 Target teacher mandatory pop-up: Off', !!r.forcePopupForTarget, (v) => patch(r._id, { forcePopupForTarget: v }))}
                {toggle('📢 Broadcast advisory to all teachers (off = private)', !!r.isBroadcastedToAll, (v) => patch(r._id, { isBroadcastedToAll: v }))}
                {toggle('👁️ Reveal complainant name to target teacher (off = anonymous)', !!r.revealReporterNames, (v) => patch(r._id, { revealReporterNames: v }))}

                <textarea
                  rows={2} placeholder="Principal notes (private)"
                  style={{ width: '100%', marginTop: 8, padding: 6, border: '1px solid #cbd5e1', borderRadius: 8 }}
                  value={notes[r._id] !== undefined ? notes[r._id] : (r.principalNotes || '')}
                  onChange={(e) => setNotes((n) => ({ ...n, [r._id]: e.target.value }))}
                />
                <button style={{ marginTop: 4 }} onClick={() => patch(r._id, { principalNotes: notes[r._id] !== undefined ? notes[r._id] : (r.principalNotes || '') })}>Save Notes</button>

                {(r.officialNotes || []).length > 0 && (
                  <ul style={{ fontSize: 12, color: '#334155', margin: '8px 0 0', paddingLeft: 18 }}>
                    {r.officialNotes.map((n, i) => <li key={i}>{n.type === 'summon' ? '📨 Summoned' : '📝 Note'} ({fmt(n.at)}, {n.by}): {n.note}</li>)}
                  </ul>
                )}
                <input
                  placeholder="Official note / summon message for teacher's file"
                  style={{ width: '100%', marginTop: 8, padding: 6, border: '1px solid #cbd5e1', borderRadius: 8 }}
                  value={official[r._id] || ''}
                  onChange={(e) => setOfficial((o) => ({ ...o, [r._id]: e.target.value }))}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => addNote(r._id, 'summon')}>Summon Teacher</button>
                  <button onClick={() => addNote(r._id, 'note')}>Add Official Note</button>
                  {r.status !== 'resolved' && <button onClick={() => patch(r._id, { status: 'resolved' })}>Mark Resolved / Clear Alert</button>}
                  {r.status !== 'dismissed' && <button onClick={() => patch(r._id, { status: 'dismissed' })}>Dismiss / False Complaint</button>}
                  {(r.status === 'resolved' || r.status === 'dismissed') && <button onClick={() => patch(r._id, { status: 'pending_review' })}>Reopen</button>}
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
